import React, { createContext, useContext, useState, useCallback, ReactNode, useRef } from 'react';

interface TektonRefreshContextType {
  // 刷新计数器，每次增加时触发重新获取数据
  refreshCounter: number;
  // ArgoCD 参数刷新计数器
  argoCDRefreshCounter: number;
  // 触发 PipelineRuns 刷新
  triggerRefresh: () => void;
  // 触发 ArgoCD 参数刷新
  triggerArgoCDRefresh: () => void;
  // 注册 ArgoCD 刷新回调
  registerArgoCDRefresh: (callback: () => void) => void;
  // 取消注册 ArgoCD 刷新回调
  unregisterArgoCDRefresh: (callback: () => void) => void;
}

const TektonRefreshContext = createContext<TektonRefreshContextType | undefined>(undefined);

interface TektonRefreshProviderProps {
  children: ReactNode;
}

/**
 * Tekton 刷新上下文 Provider
 * 用于协调 TektonPipelineCards 和 TektonPipelineRunsTable 之间的刷新
 * 同时支持刷新 ArgoCD 参数
 */
export const TektonRefreshProvider: React.FC<TektonRefreshProviderProps> = ({ children }) => {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [argoCDRefreshCounter, setArgoCDRefreshCounter] = useState(0);
  const argoCDRefreshCallbacks = useRef<Set<() => void>>(new Set());

  // 只刷新 PipelineRuns
  const triggerRefresh = useCallback(() => {
    setRefreshCounter(prev => prev + 1);
  }, []);

  // 刷新 ArgoCD 参数
  const triggerArgoCDRefresh = useCallback(() => {
    setArgoCDRefreshCounter(prev => prev + 1);
    // 调用所有注册的 ArgoCD 刷新回调
    argoCDRefreshCallbacks.current.forEach(callback => {
      try {
        callback();
      } catch (e) {
        console.error('ArgoCD refresh callback error:', e);
      }
    });
  }, []);

  const registerArgoCDRefresh = useCallback((callback: () => void) => {
    argoCDRefreshCallbacks.current.add(callback);
  }, []);

  const unregisterArgoCDRefresh = useCallback((callback: () => void) => {
    argoCDRefreshCallbacks.current.delete(callback);
  }, []);

  return (
    <TektonRefreshContext.Provider value={{
      refreshCounter,
      argoCDRefreshCounter,
      triggerRefresh,
      triggerArgoCDRefresh,
      registerArgoCDRefresh,
      unregisterArgoCDRefresh,
    }}>
      {children}
    </TektonRefreshContext.Provider>
  );
};

/**
 * 获取 Tekton 刷新上下文的 Hook
 */
export const useTektonRefresh = (): TektonRefreshContextType => {
  const context = useContext(TektonRefreshContext);
  if (!context) {
    // 如果没有 Provider 包裹，返回一个空操作的默认值
    return {
      refreshCounter: 0,
      argoCDRefreshCounter: 0,
      triggerRefresh: () => {},
      triggerArgoCDRefresh: () => {},
      registerArgoCDRefresh: () => {},
      unregisterArgoCDRefresh: () => {},
    };
  }
  return context;
};
